import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import L from "leaflet"
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap, useMapEvents } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import { MembersType } from "../RoomSlices/MembersInRoomSlice"
import { organizationProfileDefault, profileDefault } from "../../../App/GlobaleVariables"
import StorageService from "../../../App/StorageService"
import { StorageKey } from "../../../App/enums"
import HTTPService from "../../../App/HTTPService"

type GeocodedMember = MembersType & {
    latitude: number
    longitude: number
    locationLabel: string
}

type DisplayedMember = GeocodedMember & {
    displayLatitude: number
    displayLongitude: number
    overlapCount: number
    displayKey: string
    isClusterRepresentative: boolean
}

type MapViewportBounds = {
    south: number
    west: number
    north: number
    east: number
}

type MemberClusterGroup = {
    key: string
    members: GeocodedMember[]
    latitude: number
    longitude: number
}

type ParsedLocation = {
    city: string
    countryCode: string
    postalCode: string
    key: string
    label: string
}

type GeocodeLocationResult =
    | {
          status: "success"
          latitude: number
          longitude: number
      }
    | {
          status: "not-found"
      }
    | {
          status: "error"
      }

const CACHE_KEY = "room-members-geocode-cache-v1"
const FAILED_CACHE_KEY = "room-members-geocode-failed-cache-v2"
const MAP_MAX_ZOOM = 11
const FAILED_CACHE_TTL_MS = 1000 * 60 * 5
const MAX_MEMBERS_IN_VIEW = 600
const BASE_GEOCODE_REQUESTS_PER_LOAD = 60
const MID_ZOOM_GEOCODE_REQUESTS_PER_LOAD = 140
const HIGH_ZOOM_GEOCODE_REQUESTS_PER_LOAD = 260
const GEOCODE_PROGRESS_BATCH_SIZE = 10
const MAX_AVATAR_FETCHES_PER_CYCLE = 48
const AVATAR_FETCH_CONCURRENCY = 6

function getGeocodeRequestsPerLoad(zoomLevel: number) {
    if (zoomLevel >= 10) {
        return HIGH_ZOOM_GEOCODE_REQUESTS_PER_LOAD
    }

    if (zoomLevel >= 8) {
        return MID_ZOOM_GEOCODE_REQUESTS_PER_LOAD
    }

    return BASE_GEOCODE_REQUESTS_PER_LOAD
}

function toWorldPixel(latitude: number, longitude: number, zoom: number) {
    const scale = 256 * Math.pow(2, zoom)
    const x = ((longitude + 180) / 360) * scale
    const sinLatitude = Math.sin((latitude * Math.PI) / 180)
    const y =
        (0.5 - Math.log((1 + Math.min(Math.max(sinLatitude, -0.9999), 0.9999)) / (1 - Math.min(Math.max(sinLatitude, -0.9999), 0.9999))) / (4 * Math.PI)) *
        scale

    return { x, y }
}

function isInBounds(member: GeocodedMember, bounds: MapViewportBounds) {
    const latitudeInRange = member.latitude >= bounds.south && member.latitude <= bounds.north
    if (!latitudeInRange) {
        return false
    }

    if (bounds.west <= bounds.east) {
        return member.longitude >= bounds.west && member.longitude <= bounds.east
    }

    return member.longitude >= bounds.west || member.longitude <= bounds.east
}

function buildClusterGroups(members: GeocodedMember[], zoom: number): MemberClusterGroup[] {
    if (!members.length) {
        return []
    }

    if (zoom >= 11) {
        const groupedByCoordinate = new Map<string, GeocodedMember[]>()

        members.forEach((member) => {
            const coordinateKey = `${member.latitude.toFixed(6)}|${member.longitude.toFixed(6)}`
            const existingGroup = groupedByCoordinate.get(coordinateKey)
            if (existingGroup) {
                existingGroup.push(member)
            } else {
                groupedByCoordinate.set(coordinateKey, [member])
            }
        })

        return Array.from(groupedByCoordinate.entries()).map(([key, groupMembers]) => ({
            key,
            members: groupMembers,
            latitude: groupMembers[0].latitude,
            longitude: groupMembers[0].longitude,
        }))
    }

    const cellSizePixels = zoom < 6 ? 90 : zoom < 9 ? 70 : 55
    const groups = new Map<string, GeocodedMember[]>()

    members.forEach((member) => {
        const projected = toWorldPixel(member.latitude, member.longitude, zoom)
        const cellX = Math.floor(projected.x / cellSizePixels)
        const cellY = Math.floor(projected.y / cellSizePixels)
        const key = `${cellX}|${cellY}`
        const existingGroup = groups.get(key)

        if (existingGroup) {
            existingGroup.push(member)
        } else {
            groups.set(key, [member])
        }
    })

    return Array.from(groups.entries()).map(([key, groupMembers]) => {
        const latitude = groupMembers.reduce((sum, member) => sum + member.latitude, 0) / groupMembers.length
        const longitude = groupMembers.reduce((sum, member) => sum + member.longitude, 0) / groupMembers.length

        return {
            key,
            members: groupMembers,
            latitude,
            longitude,
        }
    })
}

function parseLocation(rawLocation?: string | null) {
    const location = (rawLocation || "").trim()
    if (!location) {
        return null
    }

    const match = location.match(/^(.*?)\s*\(([A-Za-z]{2})\s*-\s*([^)]+)\)$/)
    if (!match) {
        return {
            city: location,
            countryCode: "",
            postalCode: "",
            key: location.toLowerCase(),
            label: location,
        } satisfies ParsedLocation
    }

    const city = match[1].trim()
    const countryCode = match[2].trim().toLowerCase()
    const postalCode = match[3].trim()

    return {
        city,
        countryCode,
        postalCode,
        key: `${city.toLowerCase()}|${countryCode}|${postalCode}`,
        label: `${city} (${countryCode.toUpperCase()} - ${postalCode})`,
    } satisfies ParsedLocation
}

function hasPostalCode(rawLocation?: string | null) {
    const location = (rawLocation || "").trim()
    if (!location) {
        return false
    }

    const parsed = parseLocation(location)
    if (parsed?.postalCode) {
        return true
    }

    return /\b\d{4,6}\b/.test(location)
}

function getCacheValue(key: string) {
    try {
        const cachedRaw = sessionStorage.getItem(CACHE_KEY)
        if (!cachedRaw) {
            return null
        }
        const cached = JSON.parse(cachedRaw) as Record<string, { latitude: number; longitude: number }>
        return cached[key] || null
    } catch {
        return null
    }
}

function setCacheValue(key: string, value: { latitude: number; longitude: number }) {
    try {
        const cachedRaw = sessionStorage.getItem(CACHE_KEY)
        const cached = cachedRaw ? (JSON.parse(cachedRaw) as Record<string, { latitude: number; longitude: number }>) : {}
        cached[key] = value
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cached))
    } catch {
        // ignore storage failures
    }
}

function hasFailedCacheValue(key: string) {
    try {
        const cachedRaw = sessionStorage.getItem(FAILED_CACHE_KEY)
        if (!cachedRaw) {
            return false
        }
        const cached = JSON.parse(cachedRaw) as Record<string, number>
        const failedAt = cached[key]
        if (!failedAt) {
            return false
        }

        if (Date.now() - failedAt > FAILED_CACHE_TTL_MS) {
            delete cached[key]
            sessionStorage.setItem(FAILED_CACHE_KEY, JSON.stringify(cached))
            return false
        }

        return true
    } catch {
        return false
    }
}

function setFailedCacheValue(key: string) {
    try {
        const cachedRaw = sessionStorage.getItem(FAILED_CACHE_KEY)
        const cached = cachedRaw ? (JSON.parse(cachedRaw) as Record<string, number>) : {}
        cached[key] = Date.now()
        sessionStorage.setItem(FAILED_CACHE_KEY, JSON.stringify(cached))
    } catch {
        // ignore storage failures
    }
}

async function geocodeLocation(
    locationLabel: string,
    city: string,
    countryCode: string,
    postalCode: string,
    signal: AbortSignal
): Promise<GeocodeLocationResult> {
    try {
        const query = new URLSearchParams({
            location: locationLabel,
            city,
            country_code: countryCode,
            postal_code: postalCode,
        })
        const response = await HTTPService.get(`/geocode/?${query.toString()}`, { signal })
        if (response?.data?.latitude == null || response?.data?.longitude == null) {
            return { status: "not-found" }
        }

        return {
            status: "success",
            latitude: Number(response.data.latitude),
            longitude: Number(response.data.longitude),
        }
    } catch (error) {
        const isAbortError =
            signal.aborted ||
            (error instanceof DOMException && error.name === "AbortError") ||
            (typeof error === "object" && error !== null && "name" in error && (error as { name?: string }).name === "AbortError")

        if (isAbortError) {
            return { status: "error" }
        }

        return { status: "error" }
    }
}

function FitBounds(props: { points: Array<[number, number]> }) {
    const map = useMap()
    const lastPointsKeyRef = useRef("")

    useEffect(() => {
        if (!props.points.length) {
            return
        }

        const pointsKey = props.points
            .map(([latitude, longitude]) => `${latitude.toFixed(5)},${longitude.toFixed(5)}`)
            .sort()
            .join("|")

        if (pointsKey === lastPointsKeyRef.current) {
            return
        }

        lastPointsKeyRef.current = pointsKey

        const bounds = L.latLngBounds(props.points)
        map.fitBounds(bounds, { padding: [32, 32], maxZoom: MAP_MAX_ZOOM })
    }, [map, props.points])

    return null
}

function ViewportWatcher(props: { onViewChange: (zoom: number, bounds: MapViewportBounds) => void }) {
    const onViewChangeRef = useRef(props.onViewChange)
    onViewChangeRef.current = props.onViewChange

    const emitViewChange = useCallback(() => {
        const currentMap = mapRef.current
        if (!currentMap) {
            return
        }
        const bounds = currentMap.getBounds()
        onViewChangeRef.current(currentMap.getZoom(), {
            south: bounds.getSouth(),
            west: bounds.getWest(),
            north: bounds.getNorth(),
            east: bounds.getEast(),
        })
    }, [])

    const mapRef = useRef<L.Map | null>(null)

    const map = useMapEvents({
        moveend: () => {
            emitViewChange()
        },
        zoomend: () => {
            emitViewChange()
        },
    })

    useEffect(() => {
        mapRef.current = map
        emitViewChange()
    }, [map, emitViewChange])

    return null
}

function MapSizeSync() {
    const map = useMap()

    useEffect(() => {
        let isDisposed = false

        const invalidate = () => {
            if (isDisposed) {
                return
            }

            const leafletMap = map as L.Map & {
                _container?: HTMLElement | null
                _mapPane?: HTMLElement | null
            }

            const container = leafletMap._container
            if (!container || !container.isConnected || !leafletMap._mapPane) {
                return
            }

            try {
                map.invalidateSize(false)
            } catch {
                // ignore invalidate race conditions during teardown
            }
        }

        const initialFrame = window.requestAnimationFrame(invalidate)
        const delayedTimer = window.setTimeout(() => {
            window.requestAnimationFrame(invalidate)
        }, 120)

        let resizeObserver: ResizeObserver | null = null
        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(() => {
                window.requestAnimationFrame(invalidate)
            })
            const container = (map as L.Map & { _container?: HTMLElement | null })._container
            if (container) {
                resizeObserver.observe(container)
            }
        }

        return () => {
            isDisposed = true
            window.cancelAnimationFrame(initialFrame)
            window.clearTimeout(delayedTimer)
            resizeObserver?.disconnect()
        }
    }, [map])

    return null
}

function MapTileTone() {
    const map = useMap()

    useEffect(() => {
        const tilePane = map.getPane("tilePane")
        if (!tilePane) {
            return
        }

        const previousFilter = tilePane.style.filter
        const previousOpacity = tilePane.style.opacity

        tilePane.style.filter = "grayscale(0.35) saturate(0.55) brightness(1.06) contrast(0.88)"
        tilePane.style.opacity = "0.9"

        return () => {
            tilePane.style.filter = previousFilter
            tilePane.style.opacity = previousOpacity
        }
    }, [map])

    return null
}

function resolveMediaUrl(rawUrl: string) {
    if (rawUrl.startsWith("blob:")) {
        return rawUrl
    }

    if (rawUrl.startsWith("http://") || rawUrl.startsWith("https://")) {
        let normalizedUrl = rawUrl

        if (normalizedUrl.includes("3000")) {
            normalizedUrl = normalizedUrl.replace("3000", "8000").replace("https://", "http://")
        }

        return normalizedUrl
    }

    if (rawUrl.startsWith("/")) {
        if (window.location.hostname === "localhost" && rawUrl.startsWith("/media")) {
            const mediaBase = process.env.REACT_APP_BASE_API_URL!.replace("api", "")
            return `${mediaBase}${rawUrl}`.replace("//media", "/media")
        }

        return new URL(rawUrl, window.location.origin).toString()
    }

    if (rawUrl.startsWith("media/")) {
        return new URL(`/${rawUrl}`, window.location.origin).toString()
    }

    const apiBase = process.env.REACT_APP_BASE_API_URL || window.location.origin
    const apiBaseUrl = new URL(apiBase, window.location.origin)
    const mediaOrigin = `${apiBaseUrl.protocol}//${apiBaseUrl.host}`

    return new URL(rawUrl, mediaOrigin).toString()
}

function getMediaFieldUrl(mediaField: unknown): string | null {
    if (!mediaField) {
        return null
    }

    if (typeof mediaField === "string") {
        return mediaField
    }

    if (typeof mediaField === "object") {
        const candidate = mediaField as { url_path?: string; url?: string }
        return candidate.url_path || candidate.url || null
    }

    return null
}

function isProtectedMediaUrl(rawUrl: string) {
    const normalized = rawUrl.trim().toLowerCase()

    if (!normalized) {
        return false
    }

    if (normalized.startsWith("/media") || normalized.startsWith("media/")) {
        return true
    }

    if (normalized.includes("/media/") || normalized.includes("/api/media/")) {
        return true
    }

    const apiBase = (process.env.REACT_APP_BASE_API_URL || "").toLowerCase()
    if (apiBase) {
        const mediaBase = apiBase.replace("api", "")
        if (normalized.includes(mediaBase)) {
            return true
        }
    }

    return false
}

function hasPersistedCoordinates(member: MembersType) {
    return Number.isFinite(member.location_latitude) && Number.isFinite(member.location_longitude)
}

function RoomMembersMap(props: {
    members: MembersType[]
    onMemberClick: (member: MembersType) => void
}) {
    const [geocodedMembers, setGeocodedMembers] = useState<GeocodedMember[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [zoomLevel, setZoomLevel] = useState(5)
    const [viewportBounds, setViewportBounds] = useState<MapViewportBounds | null>(null)
    const [hadGeocodeServiceError, setHadGeocodeServiceError] = useState(false)
    const [deferredGeocodeLocationsCount, setDeferredGeocodeLocationsCount] = useState(0)
    const [avatarObjectUrls, setAvatarObjectUrls] = useState<Record<string, string>>({})
    const [failedAvatarUrls, setFailedAvatarUrls] = useState<Record<string, boolean>>({})
    const createdObjectUrlsRef = useRef<string[]>([])
    const accessTokenRef = useRef(StorageService.getItem(StorageKey.accessToken))
    const initialGeocodeResolvedRef = useRef(false)

    useEffect(() => {
        accessTokenRef.current = StorageService.getItem(StorageKey.accessToken)
    })

    const handleViewChange = useCallback((zoom: number, bounds: MapViewportBounds) => {
        setZoomLevel(zoom)
        setViewportBounds(bounds)
    }, [])

    const membersWithPersistedCoordinates = useMemo(() => {
        return props.members.filter((member) => hasPersistedCoordinates(member))
    }, [props.members])

    const membersNeedingGeocoding = useMemo(() => {
        return props.members.filter((member) => !hasPersistedCoordinates(member) && Boolean(member.location))
    }, [props.members])

    const hiddenMembersCount = useMemo(() => {
        return Math.max(props.members.length - membersWithPersistedCoordinates.length - membersNeedingGeocoding.length, 0)
    }, [props.members.length, membersWithPersistedCoordinates.length, membersNeedingGeocoding.length])

    const unresolvedMembersCount = useMemo(() => {
        const geocodedWithoutPersisted = Math.max(geocodedMembers.length - membersWithPersistedCoordinates.length, 0)
        return Math.max(membersNeedingGeocoding.length - geocodedWithoutPersisted, 0)
    }, [membersNeedingGeocoding.length, geocodedMembers.length, membersWithPersistedCoordinates.length])

    const legendCounts = useMemo(() => {
        return geocodedMembers.reduce(
            (accumulator, member) => {
                if (member.type === "organization") {
                    accumulator.organizations += 1
                } else {
                    accumulator.people += 1
                }

                return accumulator
            },
            { people: 0, organizations: 0 }
        )
    }, [geocodedMembers])

    const membersInViewport = useMemo(() => {
        if (!viewportBounds) {
            return geocodedMembers
        }

        return geocodedMembers.filter((member) => isInBounds(member, viewportBounds))
    }, [geocodedMembers, viewportBounds])

    const membersInViewportLimited = useMemo(() => {
        if (membersInViewport.length <= MAX_MEMBERS_IN_VIEW) {
            return membersInViewport
        }

        return membersInViewport.slice(0, MAX_MEMBERS_IN_VIEW)
    }, [membersInViewport])

    const clusteredGroups = useMemo(() => {
        return buildClusterGroups(membersInViewportLimited, zoomLevel)
    }, [membersInViewportLimited, zoomLevel])

    const isClusterMode = zoomLevel < 11

    const displayedMembers = useMemo(() => {
        const membersToDisplay: DisplayedMember[] = []

        clusteredGroups.forEach((group) => {
            const memberGroup = group.members
            const coordinateKey = group.key

            if (memberGroup.length === 1) {
                const onlyMember = memberGroup[0]
                membersToDisplay.push({
                    ...onlyMember,
                    displayLatitude: group.latitude,
                    displayLongitude: group.longitude,
                    overlapCount: 1,
                    displayKey: `${coordinateKey}|single|${onlyMember.id}`,
                    isClusterRepresentative: false,
                })
                return
            }

            if (isClusterMode) {
                const representativeMember = memberGroup[0]
                membersToDisplay.push({
                    ...representativeMember,
                    displayLatitude: group.latitude,
                    displayLongitude: group.longitude,
                    overlapCount: memberGroup.length,
                    displayKey: `${coordinateKey}|cluster`,
                    isClusterRepresentative: true,
                })
                return
            }

            const groupSize = memberGroup.length
            const representativeLatitude = group.latitude
            const representativeLatitudeRadians = (representativeLatitude * Math.PI) / 180
            const metersPerPixelAtCurrentZoom = Math.max(
                (156543.03392 * Math.cos(representativeLatitudeRadians)) / Math.pow(2, zoomLevel),
                0.000001
            )
            const targetSpreadRadiusPixels = Math.min(74, 24 + groupSize * 6)
            const radiusMeters = targetSpreadRadiusPixels * metersPerPixelAtCurrentZoom

            memberGroup.forEach((member, index) => {
                const angle = (2 * Math.PI * index) / groupSize
                const latitudeRadians = (member.latitude * Math.PI) / 180
                const metersPerDegreeLatitude = 111320
                const metersPerDegreeLongitude = Math.max(111320 * Math.cos(latitudeRadians), 0.000001)
                const latitudeOffset = (radiusMeters * Math.cos(angle)) / metersPerDegreeLatitude
                const longitudeOffset = (radiusMeters * Math.sin(angle)) / metersPerDegreeLongitude

                membersToDisplay.push({
                    ...member,
                    displayLatitude: group.latitude + latitudeOffset,
                    displayLongitude: group.longitude + longitudeOffset,
                    overlapCount: groupSize,
                    displayKey: `${coordinateKey}|member|${member.id}|${index}`,
                    isClusterRepresentative: false,
                })
            })
        })

        return membersToDisplay
    }, [clusteredGroups, isClusterMode, zoomLevel])

    const fitPoints = useMemo(() => {
        return geocodedMembers.map((member) => [member.latitude, member.longitude] as [number, number])
    }, [geocodedMembers])

    const hasClusterMarkers = useMemo(() => {
        return displayedMembers.some((member) => member.isClusterRepresentative && member.overlapCount > 1)
    }, [displayedMembers])

    const hiddenMembersHint = useMemo(() => {
        if (hiddenMembersCount === 1) {
            return "Hinweis: 1 Mitglied wird nicht auf der Karte angezeigt, weil der Standort nicht freigegeben oder nicht hinterlegt ist."
        }

        return `Hinweis: ${hiddenMembersCount} Mitglieder werden nicht auf der Karte angezeigt, weil der Standort nicht freigegeben oder nicht hinterlegt ist.`
    }, [hiddenMembersCount])

    const unresolvedMembersHint = useMemo(() => {
        if (unresolvedMembersCount === 1) {
            return "Hinweis: 1 Mitglied konnte auf der Karte nicht aufgelöst werden."
        }

        return `Hinweis: ${unresolvedMembersCount} Mitglieder konnten auf der Karte nicht aufgelöst werden.`
    }, [unresolvedMembersCount])

    const deferredGeocodeHint = useMemo(() => {
        if (deferredGeocodeLocationsCount <= 0) {
            return ""
        }

        if (zoomLevel < 8) {
            return `Hinweis: ${deferredGeocodeLocationsCount} Standorte werden beim Reinzoomen nachgeladen.`
        }

        return `Hinweis: ${deferredGeocodeLocationsCount} weitere Standorte werden schrittweise nachgeladen.`
    }, [deferredGeocodeLocationsCount, zoomLevel])

    const getMemberRawAvatarUrl = (member: MembersType) => {
        const memberType = (member.type || "").toLowerCase()
        const userAvatar = getMediaFieldUrl(member.profile_photo)
        const organizationAvatar = getMediaFieldUrl(member.photo)

        if (memberType === "organization") {
            return organizationAvatar || userAvatar || null
        }

        return userAvatar || organizationAvatar || null
    }

    const avatarUrlsToLoad = useMemo(() => {
        const urls = new Set<string>()

        membersInViewportLimited.forEach((member) => {
            const rawAvatarUrl = getMemberRawAvatarUrl(member)
            if (rawAvatarUrl && !avatarObjectUrls[rawAvatarUrl] && !failedAvatarUrls[rawAvatarUrl]) {
                urls.add(rawAvatarUrl)
            }
        })

        return Array.from(urls)
    }, [membersInViewportLimited, avatarObjectUrls, failedAvatarUrls])

    useEffect(() => {
        let isMounted = true
        const abortController = new AbortController()

        const resolveMembers = async () => {
            if (!initialGeocodeResolvedRef.current) {
                setIsLoading(true)
            }
            setHadGeocodeServiceError(false)

            const membersFromPersistedCoordinates: GeocodedMember[] = membersWithPersistedCoordinates.map((member) => ({
                ...member,
                latitude: Number(member.location_latitude),
                longitude: Number(member.location_longitude),
                locationLabel: (member.location || "Standort").trim(),
            }))

            const memberLocations = membersNeedingGeocoding
                .map((member) => ({
                    member,
                    parsedLocation: parseLocation(member.location),
                }))
                .filter((entry): entry is { member: MembersType; parsedLocation: ParsedLocation } => Boolean(entry.parsedLocation))

            const resolvedByKey = new Map<string, { latitude: number; longitude: number }>()
            const locationsToResolve = new Map<string, ParsedLocation>()
            const locationUsageCount = new Map<string, number>()

            memberLocations.forEach(({ parsedLocation }) => {
                locationUsageCount.set(parsedLocation.key, (locationUsageCount.get(parsedLocation.key) || 0) + 1)

                const fromCache = getCacheValue(parsedLocation.key)
                if (fromCache) {
                    resolvedByKey.set(parsedLocation.key, fromCache)
                    return
                }

                if (hasFailedCacheValue(parsedLocation.key)) {
                    return
                }

                if (!locationsToResolve.has(parsedLocation.key)) {
                    locationsToResolve.set(parsedLocation.key, parsedLocation)
                }
            })

            const buildMembers = (): GeocodedMember[] => {
                const geocodedMembersFromLocation = memberLocations.flatMap(({ member, parsedLocation }) => {
                    const resolved = resolvedByKey.get(parsedLocation.key)
                    if (!resolved) {
                        return []
                    }

                    return [
                        {
                            ...member,
                            latitude: resolved.latitude,
                            longitude: resolved.longitude,
                            locationLabel: parsedLocation.label,
                        },
                    ]
                })

                return [...membersFromPersistedCoordinates, ...geocodedMembersFromLocation]
            }

            const prioritizedLocations = Array.from(locationsToResolve.values())
                .sort((a, b) => (locationUsageCount.get(b.key) || 0) - (locationUsageCount.get(a.key) || 0))

            const geocodeRequestLimit = getGeocodeRequestsPerLoad(zoomLevel)

            const locationsToProcess = prioritizedLocations.slice(0, geocodeRequestLimit)
            const deferredCount = Math.max(prioritizedLocations.length - locationsToProcess.length, 0)

            if (isMounted) {
                setGeocodedMembers(buildMembers())
                setDeferredGeocodeLocationsCount(deferredCount)
                if (!initialGeocodeResolvedRef.current) {
                    setIsLoading(locationsToProcess.length > 0)
                }
            }

            let pendingSuccessesSinceUpdate = 0

            for (const parsedLocation of locationsToProcess) {
                if (abortController.signal.aborted) {
                    break
                }

                const geocodeResult = await geocodeLocation(
                    parsedLocation.label,
                    parsedLocation.city,
                    parsedLocation.countryCode,
                    parsedLocation.postalCode,
                    abortController.signal
                )

                if (geocodeResult.status === "not-found") {
                    setFailedCacheValue(parsedLocation.key)
                    continue
                }

                if (geocodeResult.status === "error") {
                    if (isMounted) {
                        setHadGeocodeServiceError(true)
                    }
                    continue
                }

                setCacheValue(parsedLocation.key, {
                    latitude: geocodeResult.latitude,
                    longitude: geocodeResult.longitude,
                })
                resolvedByKey.set(parsedLocation.key, {
                    latitude: geocodeResult.latitude,
                    longitude: geocodeResult.longitude,
                })
                pendingSuccessesSinceUpdate += 1

                if (isMounted && pendingSuccessesSinceUpdate >= GEOCODE_PROGRESS_BATCH_SIZE) {
                    setGeocodedMembers(buildMembers())
                    pendingSuccessesSinceUpdate = 0
                }
            }

            if (isMounted) {
                setGeocodedMembers(buildMembers())
                setIsLoading(false)
                initialGeocodeResolvedRef.current = true
            }
        }

        resolveMembers()

        return () => {
            isMounted = false
            abortController.abort()
        }
    }, [membersNeedingGeocoding, membersWithPersistedCoordinates, zoomLevel])

    useEffect(() => {
        let isMounted = true
        const abortController = new AbortController()

        const loadAvatars = async () => {
            if (!avatarUrlsToLoad.length) {
                return
            }

            const urlsToFetch = avatarUrlsToLoad.slice(0, MAX_AVATAR_FETCHES_PER_CYCLE)

            const nextObjectUrls: Record<string, string> = {}
            const nextFailedUrls: Record<string, boolean> = {}
            const currentAccessToken = accessTokenRef.current
            let currentIndex = 0

            const worker = async () => {
                while (currentIndex < urlsToFetch.length) {
                    const avatarUrl = urlsToFetch[currentIndex]
                    currentIndex += 1

                    const isProtectedMedia = isProtectedMediaUrl(avatarUrl)

                    if (isProtectedMedia && !currentAccessToken) {
                        nextFailedUrls[avatarUrl] = true
                        continue
                    }

                    try {
                        const response = await fetch(resolveMediaUrl(avatarUrl), {
                            signal: abortController.signal,
                            headers: currentAccessToken
                                ? {
                                      Authorization: `Bearer ${currentAccessToken}`,
                                  }
                                : undefined,
                        })

                        if (!response.ok) {
                            nextFailedUrls[avatarUrl] = true
                            continue
                        }

                        const avatarBlob = await response.blob()

                        const objectUrl = URL.createObjectURL(avatarBlob)
                        nextObjectUrls[avatarUrl] = objectUrl
                        createdObjectUrlsRef.current.push(objectUrl)
                    } catch (error) {
                        const isAbortError =
                            abortController.signal.aborted ||
                            (error instanceof DOMException && error.name === "AbortError") ||
                            (typeof error === "object" && error !== null && "name" in error && (error as { name?: string }).name === "AbortError")

                        if (isAbortError) {
                            continue
                        }

                        nextFailedUrls[avatarUrl] = true
                        // fallback images are used when authenticated media loading fails
                    }
                }
            }

            await Promise.all(Array.from({ length: Math.min(AVATAR_FETCH_CONCURRENCY, urlsToFetch.length) }, () => worker()))

            if (isMounted && Object.keys(nextObjectUrls).length) {
                setAvatarObjectUrls((currentState) => ({
                    ...currentState,
                    ...nextObjectUrls,
                }))
            }

            if (isMounted && Object.keys(nextFailedUrls).length) {
                setFailedAvatarUrls((currentState) => ({
                    ...currentState,
                    ...nextFailedUrls,
                }))
            }
        }

        loadAvatars()

        return () => {
            isMounted = false
            abortController.abort()
        }
    }, [avatarUrlsToLoad])

    useEffect(() => {
        return () => {
            createdObjectUrlsRef.current.forEach((url) => {
                URL.revokeObjectURL(url)
            })
            createdObjectUrlsRef.current = []
        }
    }, [])

    if (isLoading || props.members.length === 0) {
        return <div className="room-members__map-empty">Karte wird geladen ...</div>
    }

    if (!membersWithPersistedCoordinates.length && !membersNeedingGeocoding.length) {
        return <div className="room-members__map-empty">Keine Mitglieder mit Standort gefunden.</div>
    }

    if (!geocodedMembers.length && hadGeocodeServiceError) {
        return <div className="room-members__map-empty">Standorte konnten gerade nicht geladen werden. Bitte erneut versuchen.</div>
    }

    if (!geocodedMembers.length) {
        return <div className="room-members__map-empty">Standorte konnten nicht aufgelöst werden.</div>
    }

    const getMemberAvatar = (member: MembersType) => {
        const isOrganization = member.type === "organization"
        const rawAvatarUrl = getMemberRawAvatarUrl(member)
        const resolvedAvatarUrl = rawAvatarUrl ? resolveMediaUrl(rawAvatarUrl) : null
        const isProtectedMedia = rawAvatarUrl ? isProtectedMediaUrl(rawAvatarUrl) : false

        if (rawAvatarUrl && avatarObjectUrls[rawAvatarUrl]) {
            return avatarObjectUrls[rawAvatarUrl]
        }

        if (resolvedAvatarUrl && !isProtectedMedia) {
            return resolvedAvatarUrl
        }

        if (isOrganization) {
            return organizationProfileDefault
        }
        return profileDefault
    }

    return (
        <div className="room-members__map-wrapper">
            <div className="room-members__map-legend" aria-label="Kartenlegende">
                <div className="room-members__map-legend-item">
                    <span className="room-members__map-legend-dot room-members__map-legend-dot--user" />
                    <span>Personen ({legendCounts.people})</span>
                </div>
                <div className="room-members__map-legend-item">
                    <span className="room-members__map-legend-dot room-members__map-legend-dot--organization" />
                    <span>Organisationen ({legendCounts.organizations})</span>
                </div>
            </div>
            {hiddenMembersCount > 0 && (
                <div className="room-members__map-hint">{hiddenMembersHint}</div>
            )}
            {unresolvedMembersCount > 0 && (
                <div className="room-members__map-hint">{unresolvedMembersHint}</div>
            )}
            {deferredGeocodeHint && (
                <div className="room-members__map-hint">
                    {deferredGeocodeHint}
                </div>
            )}
            {isClusterMode && hasClusterMarkers && (
                <div className="room-members__map-hint">Hinweis: Auf einen Marker mit Zahl klicken, um die einzelnen Profile an diesem Standort zu sehen.</div>
            )}
            <MapContainer center={[51.1657, 10.4515]} zoom={5} className="room-members__map" scrollWheelZoom={true} maxZoom={MAP_MAX_ZOOM}>
                <TileLayer
                    attribution='&copy; OpenStreetMap-Mitwirkende'
                    url="https://tile.openstreetmap.de/{z}/{x}/{y}.png"
                />
                <MapTileTone />
                <MapSizeSync />
                <ViewportWatcher
                    onViewChange={handleViewChange}
                />
                <FitBounds points={fitPoints} />

                {displayedMembers.map((member) => {
                    const isOrganization = member.type === "organization"
                    const isEstimatedLocation = !hasPostalCode(member.location)
                    const hashtags = member.hashtags?.slice(0, 3).map((hashtag) => `#${hashtag}`).join(" ")
                    const avatarIcon = L.icon({
                        iconUrl: getMemberAvatar(member),
                        iconSize: [32, 32],
                        iconAnchor: [16, 32],
                        popupAnchor: [0, -30],
                        className: `room-members__map-avatar-icon ${isOrganization ? "room-members__map-avatar-icon--organization" : "room-members__map-avatar-icon--user"}`,
                    })

                    return (
                        <Marker
                            key={member.displayKey}
                            position={[member.displayLatitude, member.displayLongitude]}
                            icon={avatarIcon}
                            eventHandlers={{
                                click: (event) => {
                                    if (member.isClusterRepresentative && member.overlapCount > 1) {
                                        const map = event.target._map as L.Map
                                        const nextZoom = Math.min(MAP_MAX_ZOOM, Math.max(map.getZoom() + 2, 11))
                                        map.setView([member.latitude, member.longitude], nextZoom, { animate: true })
                                        return
                                    }

                                    const marker = event.target as L.Marker
                                    const popup = marker.getPopup?.()
                                    if (popup) {
                                        marker.openPopup()
                                    }
                                },
                            }}
                        >
                            {member.isClusterRepresentative && member.overlapCount > 1 && (
                                <Tooltip
                                    permanent={true}
                                    direction="top"
                                    offset={[0, -26]}
                                    opacity={1}
                                    interactive={false}
                                    className="room-members__map-overlap-badge"
                                >
                                    {member.overlapCount}
                                </Tooltip>
                            )}
                            {!member.isClusterRepresentative && (
                                <Popup>
                                    <div className="room-members__map-popup">
                                        <img className="room-members__map-popup-image" src={getMemberAvatar(member)} alt={member.name || "Profil"} />
                                        <div className="room-members__map-popup-name">{member.name}</div>
                                        <div className="room-members__map-popup-location">{member.locationLabel}</div>
                                        {isEstimatedLocation && (
                                            <div className="room-members__map-popup-estimate-hint">
                                                Standort geschätzt (nur Ortsangabe)
                                            </div>
                                        )}
                                        {hashtags && <div className="room-members__map-popup-meta">{hashtags}</div>}
                                        <button
                                            type="button"
                                            className="room-members__map-popup-button"
                                            onClick={(event) => {
                                                event.preventDefault()
                                                event.stopPropagation()
                                                props.onMemberClick(member)
                                            }}
                                        >
                                            Profil öffnen
                                        </button>
                                    </div>
                                </Popup>
                            )}
                        </Marker>
                    )
                })}
            </MapContainer>
        </div>
    )
}

export default RoomMembersMap